import { Component } from 'react'
import ReactDOM from 'react-dom'
import classNames from 'classnames'
import { toast, ToastContainer } from 'react-toastify'
import { HiCheckCircle, HiXCircle, HiExclamationCircle, HiInformationCircle, HiQuestionMarkCircle } from 'react-icons/hi'
import Loader from '../components/loader'
import { Title } from './title'
import { getLocale, getString } from '../locale'

import './window.css'

interface IRenderer<T> {
    container?: T
    popup?: T
    header?: T
    title?: T
    closeButton?: T
    icon?: T
    image?: T
    content?: T
    input?: T
    actions?: T
    confirmButton?: T
    cancelButton?: T
    footer?: T
}

export interface IWindowOptions {
    $rootEl?: HTMLElement
    _resolve?: (returnValue: IWindowResult) => void
    $instance?: Window
    $component?: WindowComponent
    title?: string
    icon?: 'success' | 'error' | 'warning' | 'info' | 'question'
    body?: JSX.Element | string
    timeout?: number
    hideConfirmButton?: boolean
    showConfirmButton?: boolean
    showCancelButton?: boolean
    input?: IInputType
    inputLabel?: string
    inputValue?: string
    inputOptions?: Record<string, string>
    inputValidator?: (value: string) => undefined | string
    preConfirm?: (value?: string, component?: WindowComponent) => any
    cancelButtonText?: string
    confirmButtonText?: string
    cancelButtonColor?: string
    confirmButtonColor?: string
    showLoaderOnConfirm?: boolean
    animation?: boolean
    allowOutsideClick?: boolean
    allowEscapeKey?: boolean
    allowEnterKey?: boolean
    onOpen?: () => void
    onClose?: () => void
    customClass?: IRenderer<string>
    customStyle?: IRenderer<React.CSSProperties>
}

type IInputType = 'text' | 'select' | 'number' | 'password'

interface IWindowResult {
    value?: any
    isConfirmed?: boolean
}

export class Window {
    public $options?: IWindowOptions

    public $resolve?: (returnValue: IWindowResult) => Promise<void | undefined>

    confirm = async (returnValue: IWindowResult = {}): Promise<void | undefined> => {
        if (!(await this.$validateInput(returnValue.value))) return
        if (!(await this.$preConfirm(returnValue))) return

        if (this.$resolve) {
            await this.$resolve({ ...returnValue, isConfirmed: true })
        }
    }

    cancel = async (returnValue: IWindowResult = {}): Promise<void | undefined> => {
        if (this.$resolve) {
            await this.$resolve({ ...returnValue, isConfirmed: false })
        }
    }

    clickConfirm = () => {
        return this.$options!.$component!.confirm()
    }

    fire(options: IWindowOptions | IWindowOptions['title'], body?: IWindowOptions['body'], icon?: IWindowOptions['icon']): Promise<IWindowResult> {
        if (typeof options === 'object') {
            return this.$fire(options)
        }
        return this.$fire({
            title: options,
            icon,
            body,
        })
    }

    $fire(options: IWindowOptions): Promise<IWindowResult> {
        this.$options = options

        return new Promise((resolve, reject) => {
            if (document.activeElement && (document.activeElement as HTMLElement).blur) {
                ; (document.activeElement as HTMLElement).blur()
            }

            this.$validateOptions(options, reject)

            const body = document.getElementsByTagName('body').item(0)
            const rootEl = document.createElement('fixed')
            rootEl.classList.add('dav-window')
            if (options.animation === undefined || options.animation) {
                rootEl.classList.add('animate__animated', 'animate__fadeIn')
                setTimeout(() => {
                    rootEl.classList.remove('animate__animated', 'animate__fadeIn')
                }, 1000)
            }
            if (options.customClass && options.customClass.container) {
                rootEl.classList.add(options.customClass.container)
            }
            body!.append(rootEl)

            let clickEvent: (ev: MouseEvent) => void

            if (options.allowOutsideClick === undefined || options.allowOutsideClick) {
                clickEvent = ({ target }) => {
                    if (
                        (target as HTMLElement).classList &&
                        (target as HTMLElement).classList.length > 0 &&
                        (target as HTMLElement).classList[0] === 'dav-window'
                    ) {
                        this.cancel()
                    }
                }
                rootEl.addEventListener('click', clickEvent)
            }

            const keydownEvent = (e: KeyboardEvent) => {
                if ((options.allowEscapeKey === undefined || options.allowEscapeKey) && e.key === 'Escape') {
                    this.cancel()
                }
                if (options.allowEnterKey && e.key === 'Enter') {
                    this.clickConfirm()
                }
            }
            document.addEventListener('keydown', keydownEvent)

            this.$resolve = async (returnValue: IWindowResult) => {
                if (options.onClose) await Promise.resolve(options.onClose())
                if (clickEvent) rootEl.removeEventListener('click', clickEvent)
                document.removeEventListener('keydown', keydownEvent)

                rootEl.classList.add('animate__animated', 'animate__fadeOut', 'pointer-events-none')
                this.$options!.$component!.hide()
                setTimeout(() => {
                    ReactDOM.unmountComponentAtNode(rootEl)
                    rootEl.remove()
                }, 1000)

                resolve(returnValue)
            }

            options.$rootEl = rootEl
            options.$instance = this

            ReactDOM.render(WindowComponentWrapper(options), rootEl)
        })
    }

    $validateOptions(options: IWindowOptions, reject: (reason: Error) => void) {
        if (options.input === 'select' && (options.inputOptions === undefined || Object.values(options.inputOptions).length === 0)) {
            reject(new Error('You must add options since you picked the select input.'))
        }
    }

    async $validateInput(value: string): Promise<boolean> {
        if (!this.$options!.inputValidator) return Promise.resolve(true)

        const validation = await Promise.resolve(this.$options!.inputValidator(value))
        if (validation) {
            toast.error(validation)
            return false
        }
        return true
    }

    /** @note Mutates returnValue */
    async $preConfirm(returnValue: IWindowResult): Promise<boolean> {
        if (!this.$options!.preConfirm) return true

        const newValue = await Promise.resolve(this.$options!.preConfirm(returnValue.value, this.$options!.$component!))

        if (newValue === false) return false

        returnValue.value = newValue
        return true
    }
}

function WindowComponentWrapper(options: IWindowOptions) {
    return <WindowComponent {...options} />
}

type IWindowComponentState = {
    show?: boolean
    hide?: boolean
    inputText: string
    loading: boolean
    title?: string
}

class WindowComponent extends Component<IWindowOptions, IWindowComponentState> {
    $mounted = false

    state: IWindowComponentState = {
        show: false,
        hide: false,
        inputText: '',
        loading: false,
    }

    timeout?: NodeJS.Timeout

    icons: Record<string, { component: typeof HiCheckCircle; color: string }> = {
        success: {
            component: HiCheckCircle,
            color: 'text-success',
        },
        error: {
            component: HiXCircle,
            color: 'text-danger',
        },
        warning: {
            component: HiExclamationCircle,
            color: 'text-warning',
        },
        info: {
            component: HiInformationCircle,
            color: 'text-info',
        },
        question: {
            component: HiQuestionMarkCircle,
            color: 'text-muted',
        },
    }

    constructor(props: IWindowOptions) {
        super(props)

        props.$instance!.$options!.$component = this

        this.state.title = props.title

        if (props.input === 'select' && props.inputOptions) {
            const options = Object.keys(props.inputOptions)
            if (options.length > 0) {
                this.state.inputText = options[0]
            }
        }

        if (props.inputValue) this.state.inputText = props.inputValue
    }

    confirm = async (returnValue?: IWindowResult): Promise<void | undefined> => {
        this.setState({ loading: true })
        await this.props.$instance!.confirm(returnValue)
        this.setState({ loading: false })
    }

    // @ts-ignore
    setState = (state: Partial<IWindowComponentState>) => {
        if (!this.$mounted) return
        // @ts-ignore
        super.setState(state)
    }

    show = () => {
        this.setState({ show: true })
        setTimeout(() => this.setState({ show: false }), 1000)
    }

    hide = () => {
        this.setState({ hide: true })
    }

    componentDidMount = () => {
        this.$mounted = true

        this.show()

        if (this.props.timeout) {
            this.timeout = setTimeout(() => this.props.$instance!.cancel(), this.props.timeout)
        }
        if (this.props.onOpen) {
            this.props.onOpen()
        }
    }

    componentWillUnmount = () => {
        if (this.timeout) clearTimeout(this.timeout)
        this.$mounted = false
    }

    render() {
        const { show, hide } = this.state
        return (
            <div
                dir="rtl"
                className={classNames(
                    (this.props.animation === undefined || this.props.animation) && show
                        ? 'animate__animated animate__bounceInUp'
                        : hide
                            ? 'animate__animated animate__bounceOutDown'
                            : undefined,
                    this.props.customClass && this.props.customClass.popup,
                )}
                style={this.props.customStyle?.popup}
            >
                {this.renderIcon()}
                {this.renderHeader()}
                {this.renderBody()}
                {this.renderFooter()}
                {this.renderToast()}
            </div>
        )
    }

    private renderToast() {
        return <ToastContainer position="top-center" rtl={getLocale() === 'he'} />
    }

    renderIcon() {
        const { icon } = this.props
        if (!icon) return undefined
        const iconData = this.icons[icon]
        if (!iconData) return undefined
        return (
            <div className="icon">
                <iconData.component className={iconData.color} />
            </div>
        )
    }

    renderHeader() {
        const { customClass, customStyle } = this.props
        const { title } = this.state
        if (!title) return undefined
        return (
            <div className="header">
                <div className={classNames(customClass && customClass.title)} style={customStyle?.title}>
                    <Title>{title}</Title>
                </div>
            </div>
        )
    }

    renderBody() {
        const { body, hideConfirmButton, showConfirmButton, showCancelButton, customClass, customStyle } = this.props
        return (
            <div
                className={classNames(
                    'body',
                    customClass && customClass.content,
                    (hideConfirmButton || showConfirmButton === false) && !showCancelButton ? 'mb-0' : 'mb-5',
                )}
                style={customStyle?.content}
            >
                {body}
                {this.renderInput()}
            </div>
        )
    }

    renderFooter() {
        return (
            <div className="footer flex justify-center align-center">
                {this.renderConfirmButton()}
                {this.renderCancelButton()}
            </div>
        )
    }

    renderConfirmButton() {
        const { hideConfirmButton, showConfirmButton, confirmButtonText, showLoaderOnConfirm } = this.props
        if ((showLoaderOnConfirm === undefined || showLoaderOnConfirm) && this.state.loading) return this.renderLoader()
        if (hideConfirmButton || showConfirmButton === false) return undefined
        const color = this.props.confirmButtonColor || 'green'
        return (
            <button
                // color in comment for tailwindcss
                // bg-red-600 hover:bg-red-700
                // bg-blue-600 hover:bg-blue-700
                // bg-green-600 hover:bg-green-700
                // bg-yellow-600 hover:bg-yellow-700
                // bg-indigo-600 hover:bg-indigo-700
                // bg-purple-600 hover:bg-purple-700
                // bg-pink-600 hover:bg-pink-700
                // bg-gray-600 hover:bg-gray-700
                // bg-black-600 hover:bg-black-700
                // bg-white-600 hover:bg-white-700
                className={`cursor-pointer mx-4 group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-501 duration-200 bg-${color}-600 hover:bg-${color}-700`}
                onClick={() => this.confirm({ value: this.state.inputText })}
            >
                {confirmButtonText || getString('window.confirm')}
            </button>
        )
    }

    renderLoader() {
        return (
            <div className="mx-2">
                <Loader size={8} />
            </div>
        )
    }

    renderCancelButton() {
        const { showCancelButton, cancelButtonText } = this.props
        if (!showCancelButton) return undefined
        const color = this.props.cancelButtonColor || 'red'
        return (
            <button
                onClick={() => this.props.$instance!.cancel()}
                disabled={this.state.loading}
                // color in comment for tailwindcss
                // bg-red-600 hover:bg-red-700
                // bg-blue-600 hover:bg-blue-700
                // bg-green-600 hover:bg-green-700
                // bg-yellow-600 hover:bg-yellow-700
                // bg-indigo-600 hover:bg-indigo-700
                // bg-purple-600 hover:bg-purple-700
                // bg-pink-600 hover:bg-pink-700
                // bg-gray-600 hover:bg-gray-700
                // bg-black-600 hover:bg-black-700
                // bg-white-600 hover:bg-white-700
                className={`cursor-pointer mx-4 group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-whitefocus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-501 duration-200 bg-${color}-600 hover:bg-${color}-700`}
            >
                {cancelButtonText || getString('window.decline')}
            </button>
        )
    }

    renderInput() {
        const { input } = this.props
        switch (input) {
            case 'text':
            case 'password':
                return this.renderTextInput()
            case 'select':
                return this.renderSelectInput()
            case 'number':
                return this.renderNumberInput()
            default:
                return undefined
        }
    }

    renderTextInput() {
        const { inputLabel, customClass, customStyle, input } = this.props
        const { inputText } = this.state
        return (
            <div>
                {inputLabel && <label>${inputLabel}</label>}
                <input
                    className={classNames(customClass && customClass.input)}
                    style={customStyle?.input}
                    type={input || 'text'}
                    onChange={({ target }: any) => this.setState({ inputText: target.value })}
                    value={inputText}
                    onKeyPress={(e: any) => {
                        if (e.key === 'Enter') {
                            this.confirm({ value: inputText })
                        }
                    }}
                    autoFocus
                />
            </div>
        )
    }

    renderSelectInput() {
        const { inputLabel, inputOptions, customClass, customStyle } = this.props
        const { inputText } = this.state
        if (!inputOptions) return undefined
        const options = Object.entries(inputOptions)
        return (
            <div className="dav-window-select">
                {inputLabel && <label>{inputLabel}</label>}
                <select
                    className={classNames(customClass && customClass.input)}
                    style={customStyle?.input}
                    value={inputText}
                    onChange={({ target }) => this.setState({ inputText: target.value })}
                    onKeyPress={e => {
                        if (e.key === 'Enter') {
                            this.confirm({ value: inputText })
                        }
                    }}
                    autoFocus
                >
                    {options.length === 0 && <option disabled />}
                    {Object.entries(inputOptions).map((option, index) => (
                        <option key={index} value={option[0]}>
                            {option[1]}
                        </option>
                    ))}
                </select>
            </div>
        )
    }

    renderNumberInput() {
        const { inputLabel, customClass, customStyle } = this.props
        const { inputText } = this.state
        return (
            <div className="dav-window-input">
                {inputLabel && <label>{inputLabel}</label>}
                <input
                    className={classNames('dark:text-black', customClass && customClass.input)}
                    style={customStyle?.input}
                    type="number"
                    onChange={({ target }) => this.setState({ inputText: target.value })}
                    value={inputText}
                    onKeyPress={e => {
                        if (e.key === 'Enter') {
                            this.confirm({ value: inputText })
                        }
                    }}
                    autoFocus
                />
            </div>
        )
    }
}
